import {
  BUILD_DEFAULT_VIEW_OPTIONS,
  BUILD_DEFAULT_VIEW_STORAGE_KEY,
  BUILD_PROGRAM_DEFAULT_VIEW_OPTIONS,
  BUILD_PROGRAM_DEFAULT_VIEW_STORAGE_KEY,
} from '../ducks/actionTypes';

/**
 * Build's two default entry views: the view scheduled-workout calendars open
 * on (day / week / month) and the view programs open on (day / week - the
 * Program Library has no month view).
 *
 * Stored in localStorage rather than on the user record - there is no server
 * field for them. The scheduled-workouts view is read when
 * calendarDataReducer's initialState is built, so it's in place before the
 * first render; setProgramType reads the target mode's view on each switch
 * between modes. The live viewType in Redux is a separate thing: changing a
 * preference never moves the user mid-session, and switching views never
 * rewrites a preference.
 */

/** Falls back to day view, the same default initialState carried before these
  * preferences existed. */
const FALLBACK_VIEW = 'dayView';

/** Mirrors the app-wide very-small-screen breakpoint in
  * shared/hooks/useIsMobile.js. Duplicated rather than shared because this runs
  * outside React (module load, action creators), where a hook can't. */
const PHONE_QUERY = '(max-width: 480px)';

/** Choices whose write failed (blocked storage, full quota), by storage key.
  * Unlike appearance, these preferences aren't held in Redux - every read goes
  * back to storage - so without this a failed save would be forgotten on the
  * next mode switch or Settings open, not just on reload. */
const unsavedViews = {};

const readView = (key, options) => {
  if (Object.prototype.hasOwnProperty.call(unsavedViews, key)) {
    return unsavedViews[key];
  }
  try {
    const stored = window.localStorage.getItem(key);
    return options.includes(stored) ? stored : FALLBACK_VIEW;
  } catch (e) {
    /** Storage unavailable (private mode, blocked cookies): use the default. */
    return FALLBACK_VIEW;
  }
};

const writeView = (key, options, view) => {
  const next = options.includes(view) ? view : FALLBACK_VIEW;
  try {
    window.localStorage.setItem(key, next);
    delete unsavedViews[key];
  } catch (e) {
    /** Couldn't persist: hold the choice for the rest of this page session. */
    unsavedViews[key] = next;
  }
  return next;
};

/**
 * The stored scheduled-workouts preference, or 'dayView' when nothing valid is
 * stored. This is what the Settings dropdown shows - the view the coach
 * actually picked, not the view this device will open on (see
 * resolveEntryViewType).
 */
export const readStoredDefaultView = () => readView(
  BUILD_DEFAULT_VIEW_STORAGE_KEY,
  BUILD_DEFAULT_VIEW_OPTIONS,
);

/**
 * The view a scheduled-workout calendar should actually open on. Month view is
 * hidden from the view menu on phones (HeaderDropdownMenu), so it can't be an
 * entry view there either - fall back to day view without rewriting storage,
 * so the same browser on a wider screen still honours Month.
 */
export const resolveEntryViewType = () => {
  const stored = readStoredDefaultView();
  if (stored !== 'monthView') {
    return stored;
  }
  try {
    return window.matchMedia(PHONE_QUERY).matches ? FALLBACK_VIEW : stored;
  } catch (e) {
    /** No matchMedia (older browser, non-DOM env): assume a full-size screen. */
    return stored;
  }
};

/** Persist the scheduled-workouts preference, normalising anything
  * unrecognised to day view. */
export const writeStoredDefaultView = (view) => writeView(
  BUILD_DEFAULT_VIEW_STORAGE_KEY,
  BUILD_DEFAULT_VIEW_OPTIONS,
  view,
);

/** The stored program preference, or 'dayView' when nothing valid is stored.
  * No phone clamp needed: Month isn't an option. */
export const readStoredProgramDefaultView = () => readView(
  BUILD_PROGRAM_DEFAULT_VIEW_STORAGE_KEY,
  BUILD_PROGRAM_DEFAULT_VIEW_OPTIONS,
);

/** Persist the program preference, normalising anything unrecognised
  * (including Month) to day view. */
export const writeStoredProgramDefaultView = (view) => writeView(
  BUILD_PROGRAM_DEFAULT_VIEW_STORAGE_KEY,
  BUILD_PROGRAM_DEFAULT_VIEW_OPTIONS,
  view,
);
